import React, { Component } from 'react';
import { withTheme, withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';

// ui/ux
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

// ours
import { uuidv4, stopWords } from '../../../../common';

const styles = (theme) => ({
  indent: {
    marginLeft: theme.spacing(4)
  },
  flex: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column'
  },
  excerpt: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    fontWeight: 'bold'
  },
  highlight: {
    backgroundColor: 'yellow'
  }
});

class MapQuestions extends Component {
  constructor(props) {
    super(props);
    console.log('MapQuestions', JSON.stringify({ ...props, classes: null, theme: null, onComplete: null }));
    this.state = {
      id: uuidv4(),
      selectedIdx: null
    };
    this._QA_STOP_WORDS = ['TEXT', 'PHOTO', 'GEOPOINT'];
    this.onRadioChange = this.onRadioChange.bind(this);
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.formQuestion !== this.props.formQuestion) {
      this.setState({
        selectedIdx: null,
        id: uuidv4()
      });
    }
    if (prevState.id !== this.state.id) {
      this.highlight();
    }
  }
  componentDidMount() {
    this.highlight();
  }
  highlight() {
    const { id } = this.state;
    const captions = document.querySelectorAll(`#rg-selquestions-${id} .MuiTypography-caption`);

    if (!captions || captions.length === 0) {
      return;
    }

    const { classes, formQuestion } = this.props;
    const words = (formQuestion.opts && formQuestion.opts.length > 0 ? formQuestion.opts.map((d) => d.value) : formQuestion.labels)
      .join(' ')
      .toLowerCase()
      .replace(/[-_]/g, ' ')
      .replace(/[^a-zA-Z\s]+/g, '')
      .split(/\s/g)
      .map((d) => d.trim())
      .filter((d) => d.length > 2)
      .filter((d) => !stopWords.includes(d));

    for (const word of words) {
      const re = new RegExp(word, 'g');
      for (const caption of captions) {
        caption.innerHTML = caption.innerHTML.replace(re, `<span class='${classes.highlight}'>$&</span>`);
      }
    }
  }
  renderButtons(nextEnabled) {
    const { selectedIdx } = this.state;
    const { musts, formQuestion, onComplete, existingQuestions } = this.props;
    const canSkip = formQuestion.name in musts;

    return (
      <Grid container justifyContent="flex-end" spacing={2}>
        <Grid item>
          <Tooltip title="Applicable if question was used to divide form data" arrow>
            <span>
              <Button
                disabled={!canSkip}
                variant="outlined"
                color="secondary"
                onClick={() => {
                  onComplete(formQuestion, false);
                }}
              >
                Skip
              </Button>
            </span>
          </Tooltip>
        </Grid>

        <Grid item>
          <Tooltip title="Applicable if question label has not been seen before" arrow>
            <span>
              <Button
                disabled={formQuestion.configured}
                variant="outlined"
                color="secondary"
                onClick={() => {
                  onComplete(formQuestion, null);
                }}
              >
                No Match
              </Button>
            </span>
          </Tooltip>
        </Grid>

        <Grid item>
          <Button
            disabled={!nextEnabled}
            variant="outlined"
            color="secondary"
            onClick={() => {
              onComplete(formQuestion, existingQuestions[selectedIdx]);
            }}
          >
            Next
          </Button>
        </Grid>
      </Grid>
    );
  }
  onRadioChange(e) {
    this.setState({
      selectedIdx: +e.target.value
    });
  }
  render() {
    const { classes, formQuestion, topic, existingQuestions } = this.props;
    const { id, selectedIdx } = this.state;
    const { input_type, labels, opts } = formQuestion;

    let discrete = '';
    if (!this._QA_STOP_WORDS.includes(input_type) && opts) {
      discrete = (
        <React.Fragment>
          <span>{' This question has discrete answers: '}</span>
          {opts.map((d, i) => (
            <React.Fragment key={d.value}>
              {i > 0 ? ', ' : ''}"<span className={classes.excerpt}>{d.value}</span>"
            </React.Fragment>
          ))}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Typography>
          {labels.length > 1 ? 'Have these questions "' : 'Has this question "'}
          <span className={classes.excerpt}>{labels.join('", "')}</span>
          {'" of type "'}
          <span className={classes.excerpt}>{input_type}</span>
          {'" or similar already been aggregated for the topic "'}
          <span className={classes.excerpt}>{topic}</span>
          {'"?'}
          {discrete}
        </Typography>

        {this.renderButtons(selectedIdx !== null)}
        <FormControl component="fieldset">
          <RadioGroup id={`rg-selquestions-${id}`} value={selectedIdx} name="rg-selquestions" onChange={this.onRadioChange}>
            {existingQuestions.map((d, i) => (
              <React.Fragment key={`${id}-${d.display_key}`}>
                <FormControlLabel value={i} control={<Radio />} label={d.display_key} />
                {d.input_keys.map((d) => (
                  <Typography key={d} className={classes.indent} variant="caption">
                    {d}
                  </Typography>
                ))}
                <Box className={clsx(classes.indent, classes.flex)}>
                  {d.possible_answers &&
                    d.possible_answers.map((d) => (
                      <Typography key={d} className={classes.indent} variant="caption">
                        {d}
                      </Typography>
                    ))}
                </Box>
              </React.Fragment>
            ))}
          </RadioGroup>
        </FormControl>
        {this.renderButtons(selectedIdx !== null)}
      </React.Fragment>
    );
  }
}

MapQuestions.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,

  // data
  topic: PropTypes.string.isRequired,
  formQuestion: PropTypes.object.isRequired,
  existingQuestions: PropTypes.array.isRequired,
  musts: PropTypes.object,

  // callbacks
  onComplete: PropTypes.func.isRequired
};

MapQuestions.defaultProps = {
  musts: {}
};

export default withTheme(withStyles(styles)(MapQuestions));
